import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Loader2, CheckCircle2, AlertCircle, Download, Printer, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 20;

export default function CheckoutSuccess() {
  const { search } = useLocation();
  const sessionId = new URLSearchParams(search).get("session_id");
  const [state, setState] = useState({ status: "checking", orderId: null, error: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", error: "Missing session_id." });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;

        if (data.payment_status === "paid" && data.order_id) {
          setState({ status: "paid", orderId: data.order_id, error: null });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", error: "Session expired before payment completed." });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "timeout", error: "Payment is still processing. Check your email shortly." });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (_e) {
        if (!cancelled) setState({ status: "error", error: "Couldn't verify your payment. Please contact support." });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="bg-black text-zinc-100 min-h-screen pt-16 pb-24">
      <div className="container-pl max-w-xl text-center">
        {state.status === "checking" && (
          <div data-testid="checkout-checking">
            <Loader2 className="h-10 w-10 animate-spin text-amber-400 mx-auto mb-6"/>
            <p className="overline mb-4">Confirming payment</p>
            <h1 className="display-serif text-3xl sm:text-4xl text-zinc-50 mb-3">One moment…</h1>
            <p className="text-zinc-400">Securely verifying your transaction with Stripe.</p>
          </div>
        )}
        {state.status === "paid" && (
          <div data-testid="checkout-paid">
            <CheckCircle2 className="h-12 w-12 text-amber-400 mx-auto mb-6" strokeWidth={1.5}/>
            <p className="overline mb-4">Payment confirmed</p>
            <h1 className="display-serif text-4xl sm:text-5xl text-zinc-50 leading-tight mb-3 accent-rule inline-block">
              Your packet is ready.
            </h1>
            <p className="text-zinc-300 mt-4 leading-relaxed">
              We've generated your formatted document and emailed a copy. You can also download it directly below.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center" data-testid="post-payment-actions">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${state.orderId}/pdf`} target="_blank" rel="noreferrer">
                <Button size="lg" className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white" data-testid="download-pdf-btn">
                  <Download className="mr-2 h-4 w-4" strokeWidth={1.5}/> Download PDF
                </Button>
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${state.orderId}/pdf?inline=true`}
                target="_blank"
                rel="noreferrer"
              >
                <Button size="lg" variant="outline" className="rounded-sm border-amber-400 text-amber-400 hover:bg-blue-950/30" data-testid="print-pdf-btn">
                  <Printer className="mr-2 h-4 w-4" strokeWidth={1.5}/> Print
                </Button>
              </a>
              <Link to="/services">
                <Button size="lg" variant="ghost" className="rounded-sm text-zinc-400 hover:text-amber-400">
                  Browse services <ArrowRight className="ml-2 h-4 w-4"/>
                </Button>
              </Link>
            </div>
            <p className="text-[11px] text-zinc-500 mt-3 max-w-md mx-auto">
              <span className="text-zinc-400">Tip:</span> click <span className="text-amber-400">Print</span> to open the PDF in your browser, then use <span className="font-mono">Cmd / Ctrl + P</span> to print or save a copy.
            </p>
            <p className="text-[11px] text-zinc-500 italic mt-8 max-w-md mx-auto">
              We are not attorneys and do not provide legal advice. This document is administrative preparation only.
            </p>
          </div>
        )}
        {(state.status === "expired" || state.status === "error" || state.status === "timeout") && (
          <div data-testid="checkout-error">
            <AlertCircle className="h-10 w-10 text-amber-400 mx-auto mb-6" strokeWidth={1.5}/>
            <p className="overline mb-4">{state.status === "timeout" ? "Still processing" : "Payment issue"}</p>
            <h1 className="display-serif text-3xl text-zinc-50 mb-3">{state.error}</h1>
            <p className="text-zinc-400 mb-6">If you believe this is in error, please contact us at <a href="mailto:Darrell@paralegalvoicesofnewyork.com" className="text-amber-400 hover:underline">Darrell@paralegalvoicesofnewyork.com</a></p>
            <Link to="/services">
              <Button variant="outline" className="rounded-sm border-amber-400 text-amber-400 hover:bg-blue-950/30">Back to services</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
